/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';

import CycleButton from '@react/react-spectrum/CycleButton';
import { MenuItem } from '@react/react-spectrum/Menu';
import DropdownButton from '@react/react-spectrum/DropdownButton';
import ChevronDown from '@react/react-spectrum/Icon/ChevronDown';
import SortOrderDown from '@react/react-spectrum/Icon/SortOrderDown';
import SortOrderUp from '@react/react-spectrum/Icon/SortOrderUp';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import { defineMessages, useIntl } from 'react-intl';

import { useProductAdminContext } from './ProductAdminContext';
import { trackEvent } from '../../utils/tracking';
import { event_productadmin_sortdirection, event_productadmin_sortfield } from '../../utils/trackingEvents';

import classes from './Sorting.scss';

const messages = defineMessages({
    labelName: {
        id: 'label.name',
        defaultMessage: 'Name'
    },
    labelPrice: {
        id: 'label.price',
        defaultMessage: 'Price'
    },
    labelSortBy: {
        id: 'label.sortBy',
        defaultMessage: 'Sort by'
    },
    labelAscending: {
        id: 'label.ascending',
        defaultMessage: 'Ascending'
    },
    labelDescending: {
        id: 'label.descending',
        defaultMessage: 'Descending'
    }
});

const Sorting = () => {
    const [{ config, sortField, sortDirection }, { setSortField, setSortDirection }] = useProductAdminContext();
    const { formatMessage } = useIntl();

    let sortFields = [{ id: 'name', label: formatMessage(messages.labelName) }];

    // Add price sorting option for products
    if (config !== null) {
        sortFields.push({ id: 'price', label: formatMessage(messages.labelPrice) });
    }

    const selectedSortField = sortFields.find(f => f.id === sortField) || sortFields[0];

    return (
        <div className={classes.sorting}>
            <FieldLabel label={formatMessage(messages.labelSortBy)} position="left" className={classes.label}>
                <DropdownButton
                    className={classes.dropdown}
                    label={selectedSortField.label}
                    icon={<ChevronDown />}
                    onSelect={field => {
                        trackEvent(event_productadmin_sortfield);
                        setSortField(field);
                    }}
                >
                    {sortFields.map(f => (
                        <MenuItem role="menuitemradio" key={`sort-${f.id}`} value={f.id} selected={f.id === sortField}>
                            {f.label}
                        </MenuItem>
                    ))}
                </DropdownButton>
            </FieldLabel>
            <CycleButton
                onAction={direction => {
                    trackEvent(event_productadmin_sortdirection);
                    setSortDirection(direction === 'asc' ? 1 : 0);
                }}
                actions={[
                    { name: 'asc', icon: <SortOrderDown />, label: formatMessage(messages.labelAscending) },
                    { name: 'desc', icon: <SortOrderUp />, label: formatMessage(messages.labelDescending) }
                ]}
                // Display the action that should be triggered on click which is opposite of the current direction
                action={sortDirection === 1 ? 'desc' : 'asc'}
            />
        </div>
    );
};

export default Sorting;
